import React from "react"
import { Link } from "react-router-dom"
import { useAuth0 } from "@auth0/auth0-react"
import { Bell, LayoutDashboard, Moon, ShieldCheck, SlidersHorizontal, User } from "lucide-react"
import { useUserProfile } from "../../hooks/useUserProfile"
import useNotificationPreferences from "../../hooks/useNotificationPreferences"

const SettingCard = ({ icon, title, description, action }) => (
  <article className="tyk-card p-5">
    <div className="flex items-start gap-4">
      <span className="inline-grid h-11 w-11 shrink-0 place-items-center rounded-xl border border-cyan-300/25 bg-cyan-300/10 text-cyan-200">
        {icon}
      </span>
      <div className="min-w-0">
        <h2 className="m-0 text-lg font-bold text-slate-50">{title}</h2>
        <p className="mt-2 text-sm leading-6 text-slate-300">{description}</p>
        {action && <div className="mt-4">{action}</div>}
      </div>
    </div>
  </article>
)

export default function SettingsPage() {
  const { user, logout } = useAuth0()
  const { profile } = useUserProfile()
  const { enabledCount, options, preferences, setPreference } = useNotificationPreferences(user?.sub)
  const displayName = profile?.userFname || user?.name || "Your account"

  return (
    <main className="tyk-page">
      <div className="tyk-page-shell">
        <section className="tyk-hero-panel">
          <div>
            <span className="tyk-kicker">
              <SlidersHorizontal size={16} aria-hidden="true" />
              Settings
            </span>
            <h1 className="tyk-hero-title">{displayName}</h1>
            <p className="tyk-hero-copy">
              Manage your profile, notification preferences, dashboard shortcuts, and account access from one calm place.
            </p>
          </div>
          <Link to="/dashboard" className="tyk-action">
            <LayoutDashboard size={16} aria-hidden="true" />
            Dashboard
          </Link>
        </section>

        <section className="tyk-grid">
          <SettingCard
            icon={<User size={20} aria-hidden="true" />}
            title="Profile"
            description="Review your public identity and keep the details other members see up to date."
            action={<Link to="/user-profile" className="tyk-secondary-action">View profile</Link>}
          />
          <SettingCard
            icon={<Bell size={20} aria-hidden="true" />}
            title="Notifications"
            description={`${enabledCount} alert categories are active on this browser. These preferences shape the mobile alert experience as notification channels expand.`}
            action={
              <div className="notification-preferences">
                {options.map((option) => (
                  <label key={option.id} className="notification-preference-row">
                    <span>
                      <strong>{option.title}</strong>
                      <small>{option.description}</small>
                    </span>
                    <input
                      type="checkbox"
                      checked={Boolean(preferences[option.id])}
                      onChange={(event) => setPreference(option.id, event.target.checked)}
                    />
                  </label>
                ))}
              </div>
            }
          />
          <SettingCard
            icon={<Moon size={20} aria-hidden="true" />}
            title="Experience"
            description="Theme and language controls live in the app header. This page will collect deeper display preferences over time."
            action={<span className="tyk-pill">Header controls active</span>}
          />
          <SettingCard
            icon={<ShieldCheck size={20} aria-hidden="true" />}
            title="Account access"
            description="Sign out from this browser session when you are done working on shared devices."
            action={
              <button
                type="button"
                className="tyk-secondary-action"
                onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
              >
                Sign out
              </button>
            }
          />
        </section>
      </div>
    </main>
  )
}
